import React, { useState, useEffect } from 'react';
import {
  FaUser, FaEnvelope, FaCalendarAlt, FaMapMarkedAlt, FaRuler, FaCheck,
  FaEyeSlash, FaCamera, FaCrown, FaCompass, FaRocket, FaCreditCard,
  FaDownload, FaPaperclip, FaMagic, FaInfinity, FaHashtag, FaCopy, FaGift,
  FaGlobe, FaLock, FaChevronDown, FaChevronUp, FaEye, FaTimes,
  FaBell, FaBellSlash, FaClock, FaExclamationTriangle, FaShieldAlt, FaBullhorn
} from 'react-icons/fa';
import { updateUserProfile, createBillingPortalSession, changePassword } from '../api/client';

// Supported languages for itinerary generation
const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'it', name: 'Italian', native: 'Italiano' },
  { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'ja', name: 'Japanese', native: '日本語' },
  { code: 'zh', name: 'Chinese', native: '中文' },
  { code: 'ko', name: 'Korean', native: '한국어' },
  { code: 'ru', name: 'Russian', native: 'Русский' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'nl', name: 'Dutch', native: 'Nederlands' },
  { code: 'pl', name: 'Polish', native: 'Polski' },
  { code: 'tr', name: 'Turkish', native: 'Türkçe' },
];

const UserProfile = ({ user, onProfileUpdate, onBack, onViewPricing }) => {
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    distance_radius: user.distance_radius || 50,
    distance_unit: user.distance_unit || 'miles',
    blur_faces: user.blur_faces || false,
    preferred_language: user.preferred_language || 'en',
    enable_itinerary_notifications: user.enable_itinerary_notifications !== false,
    enable_trip_reminders: user.enable_trip_reminders !== false,
    enable_travel_advisories: user.enable_travel_advisories !== false,
    enable_security_alerts: user.enable_security_alerts !== false,
    marketing_consent: user.marketing_consent || false
  });
  const [loading, setLoading] = useState(false);

  // Collapsible section states
  const [notifSectionOpen, setNotifSectionOpen] = useState(true);
  const [scheduledSectionOpen, setScheduledSectionOpen] = useState(true);
  const [securitySectionOpen, setSecuritySectionOpen] = useState(true);
  const [marketingSectionOpen, setMarketingSectionOpen] = useState(true);
  const [billingLoading, setBillingLoading] = useState(false);
  const [message, setMessage] = useState('');
  // promoStatus removed — promotional offer sunset
  const [copied, setCopied] = useState(false);

  // Change password state
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation checks
  const passwordChecks = {
    minLength: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    digit: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*()_+\-=\[\]{}|;:'",.<>?/`~\\]/.test(newPassword),
    match: newPassword && confirmPassword && newPassword === confirmPassword,
  };
  const allPasswordChecksPass = Object.values(passwordChecks).every(Boolean);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    if (!currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    if (!allPasswordChecksPass) {
      setPasswordError('Please meet all password requirements');
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(newPassword, confirmPassword, currentPassword);
      setPasswordMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setPasswordOpen(false);
        setPasswordMessage('');
      }, 2000);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        setPasswordError(detail.map(e => e.msg || e).join('. '));
      } else {
        setPasswordError(detail || 'Failed to change password. Please try again.');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const isIndividual = user.subscription_tier === 'individual';
  const isGroup = user.subscription_tier === 'group';
  const isPromotional = user.subscription_tier === 'promotional';
  const isAdmin = user.subscription_tier === 'admin';
  const isCommercial = ['starter_agent', 'professional_agent', 'enterprise_agency'].includes(user.subscription_tier);
  const isPremium = isIndividual || isGroup || isPromotional || isAdmin || isCommercial || user.subscription_tier === 'unlimited';

  const handleManageBilling = async () => {
    setBillingLoading(true);
    try {
      const { portal_url } = await createBillingPortalSession();
      window.location.href = portal_url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      setMessage('Failed to open billing portal. Please try again.');
      setBillingLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'distance_radius' ? parseInt(value, 10) : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const updatedUser = await updateUserProfile(formData);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setMessage('Profile updated successfully!');

      if (onProfileUpdate) {
        onProfileUpdate(updatedUser);
      }

      setTimeout(() => {
        if (onBack) {
          onBack();
        }
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="bg-gradient-ocean rounded-2xl p-8 mb-8 text-white shadow-elevated">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center gap-3">
              <FaUser className="text-4xl" />
              Profile Settings
            </h1>
            <p className="text-white/90 text-lg">Manage your account and preferences</p>
          </div>
          <button
            onClick={onBack}
            className="hidden md:block bg-white text-secondary-600 px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-200"
          >
            ← Back
          </button>
        </div>
      </div>

      {/* User Info Card */}
      <div className="bg-white rounded-xl shadow-medium overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-6 py-4 border-b border-primary-200">
          <h2 className="text-xl font-bold text-primary-700 flex items-center gap-2">
            <FaUser className="text-primary-500" />
            Account Information
          </h2>
        </div>
        <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
            <div className="w-12 h-12 bg-gradient-secondary rounded-full flex items-center justify-center">
              <FaEnvelope className="text-white text-xl" />
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wide">Email</p>
              <p className="font-semibold text-neutral-800">{user.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
              <FaHashtag className="text-white text-xl" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-purple-600 uppercase tracking-wide font-medium">Your Member ID</p>
              <p className="font-bold text-purple-800 text-lg">#{user.member_code || 'N/A'}</p>
              <p className="text-xs text-purple-500">Share this ID so others can add you to their trips</p>
            </div>
            {user.member_code && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`#${user.member_code}`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className={`p-2 rounded-lg transition-all ${
                  copied
                    ? 'text-green-600 bg-green-100'
                    : 'text-purple-500 hover:text-purple-700 hover:bg-purple-100'
                }`}
                title={copied ? 'Copied!' : 'Copy Member ID'}
              >
                {copied ? <FaCheck /> : <FaCopy />}
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg">
            <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
              <FaCalendarAlt className="text-white text-xl" />
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wide">Member Since</p>
              <p className="font-semibold text-neutral-800">
                {new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Subscription Card */}
      <div className={`rounded-xl shadow-medium p-6 mb-8 ${isPremium ? (isPromotional ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white') : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/20">
          <h2 className={`text-xl font-bold flex items-center gap-2 ${isPremium ? 'text-white' : 'text-neutral-800'}`}>
            {isPremium ? <FaCrown className="text-yellow-300" /> : <FaCompass />}
            Subscription
          </h2>
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
            isPremium ? 'bg-white/20 text-white' : 'bg-neutral-100 text-neutral-600'
          }`}>
            {isAdmin ? 'Admin' : isPromotional ? 'Early Adopter' : isIndividual ? 'Individual' : isGroup ? 'Group' : isCommercial ? user.subscription_tier.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : user.subscription_tier === 'demo' ? 'Demo' : 'Free'}
          </span>
        </div>

        {isPremium ? (
          // Premium Tier Display
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <FaDownload className="text-2xl mx-auto mb-1 text-white/80" />
                <p className="text-xs text-white/70">Offline Access</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <FaPaperclip className="text-2xl mx-auto mb-1 text-white/80" />
                <p className="text-xs text-white/70">Attachments</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <FaMagic className="text-2xl mx-auto mb-1 text-white/80" />
                <p className="text-xs text-white/70">AI Optimize</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <FaInfinity className="text-2xl mx-auto mb-1 text-white/80" />
                <p className="text-xs text-white/70">Unlimited Trips</p>
              </div>
            </div>

            {user.subscription_end && (
              <p className="text-white/80 text-sm mb-4">
                {isPromotional ? (
                  <>
                    Promotional access ends on{' '}
                    {new Date(user.subscription_end).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </>
                ) : (
                  <>
                    {user.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'} plan renews on{' '}
                    {new Date(user.subscription_end).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </>
                )}
              </p>
            )}

            {(isIndividual || isGroup || isCommercial) && (
              <button
                onClick={handleManageBilling}
                disabled={billingLoading}
                className="flex items-center gap-2 bg-white text-primary-600 px-6 py-2 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50"
              >
                {billingLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <FaCreditCard />
                    Manage Billing
                  </>
                )}
              </button>
            )}
          </div>
        ) : (
          // Explorer Tier Display - Upgrade Prompt
          <div>
            <p className="text-neutral-600 mb-4">
              You're on the free plan with limited features. Upgrade to unlock the full experience!
            </p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="flex items-center gap-2 text-neutral-500 text-sm">
                <FaCheck className="text-green-500" />
                <span>3 itineraries/month</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-400 text-sm">
                <span className="w-4 h-4 rounded-full bg-neutral-200 flex items-center justify-center text-xs">-</span>
                <span className="line-through">Offline access</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-500 text-sm">
                <FaCheck className="text-green-500" />
                <span>Basic planning</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-400 text-sm">
                <span className="w-4 h-4 rounded-full bg-neutral-200 flex items-center justify-center text-xs">-</span>
                <span className="line-through">Attachments</span>
              </div>
            </div>

            <button
              onClick={onViewPricing}
              className="flex items-center gap-2 bg-gradient-primary text-white px-6 py-3 rounded-lg font-semibold shadow-travel hover:shadow-elevated hover:scale-105 transition-all"
            >
              <FaRocket />
              Upgrade Subscription
            </button>
          </div>
        )}
      </div>

      {/* Edit Profile Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="bg-white rounded-xl shadow-medium overflow-hidden">
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-200">
            <h2 className="text-xl font-bold text-primary-700 flex items-center gap-2">
              <FaUser className="text-primary-500" />
              Personal Information
            </h2>
          </div>
          <div className="p-6 space-y-5">

          <div>
            <label htmlFor="full_name" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
              <FaUser className="text-primary-500" />
              Full Name
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all disabled:opacity-50"
              disabled={loading}
            />
          </div>
          </div>
        </div>

        {/* Travel Preferences */}
        <div className="bg-white rounded-xl shadow-medium overflow-hidden">
          <div className="bg-gradient-to-r from-secondary-50 to-secondary-100 px-6 py-4 border-b border-secondary-200">
            <h2 className="text-xl font-bold text-secondary-700 flex items-center gap-2">
              <FaMapMarkedAlt className="text-secondary-500" />
              Travel Preferences
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <p className="text-sm text-neutral-600 p-4 bg-secondary-50 rounded-lg border-l-4 border-secondary-400">
              Configure how nearby attractions are discovered in your itineraries. The app will find interesting places within your specified radius.
            </p>

          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-3">
              <FaRuler className="text-secondary-500" />
              Search Radius for Nearby Attractions
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <input
                  type="number"
                  id="distance_radius"
                  name="distance_radius"
                  value={formData.distance_radius}
                  onChange={handleChange}
                  min="1"
                  max="500"
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-secondary-500 focus:ring-4 focus:ring-secondary-100 outline-none transition-all disabled:opacity-50"
                  disabled={loading}
                />
              </div>

              <div>
                <select
                  id="distance_unit"
                  name="distance_unit"
                  value={formData.distance_unit}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-secondary-500 focus:ring-4 focus:ring-secondary-100 outline-none transition-all disabled:opacity-50 bg-white"
                  disabled={loading}
                >
                  <option value="miles">Miles</option>
                  <option value="kilometers">Kilometers</option>
                </select>
              </div>
            </div>

            <p className="text-sm text-neutral-500 mt-2">
              Range: 1-500 {formData.distance_unit}. This determines how far from your route to search for attractions.
            </p>
          </div>
          </div>
        </div>

        {/* Photo Privacy Settings */}
        <div className="bg-white rounded-xl shadow-medium overflow-hidden">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b border-purple-200">
            <h2 className="text-xl font-bold text-purple-700 flex items-center gap-2">
              <FaCamera className="text-purple-500" />
              Photo Privacy
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <p className="text-sm text-neutral-600 p-4 bg-primary-50 rounded-lg border-l-4 border-primary-400">
              Control how faces appear in your itinerary photos. When enabled, all faces in your photos will be automatically blurred for privacy.
            </p>

          <div className="bg-neutral-50 rounded-lg p-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="blur_faces"
                checked={formData.blur_faces}
                onChange={handleChange}
                disabled={loading}
                className="w-5 h-5 mt-0.5 rounded border-2 border-neutral-300 text-primary-500 focus:ring-2 focus:ring-primary-400 transition-colors cursor-pointer disabled:opacity-50"
              />
              <div>
                <span className="font-semibold text-neutral-800 flex items-center gap-2 mb-1">
                  <FaEyeSlash className="text-primary-500" />
                  Blur faces in all photos
                </span>
                <p className="text-sm text-neutral-600">
                  {formData.blur_faces
                    ? "Faces will be blurred in all your itinerary photos for privacy protection"
                    : "Faces will be visible in your photos"}
                </p>
              </div>
            </label>
          </div>
          </div>
        </div>

        {/* Itinerary Language Settings */}
        <div className="bg-white rounded-xl shadow-medium overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-emerald-100 px-6 py-4 border-b border-green-200">
            <h2 className="text-xl font-bold text-green-700 flex items-center gap-2">
              <FaGlobe className="text-green-500" />
              Itinerary Language
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <p className="text-sm text-neutral-600 p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
              Choose the language for your AI-generated itineraries. New itineraries will be created in your preferred language.
            </p>

          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-3">
              <FaGlobe className="text-green-500" />
              Preferred Language
            </label>
            <select
              id="preferred_language"
              name="preferred_language"
              value={formData.preferred_language}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all disabled:opacity-50 bg-white"
              disabled={loading}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name} ({lang.native})
                </option>
              ))}
            </select>
            <p className="text-sm text-neutral-500 mt-2">
              This affects new itineraries only. You can translate existing itineraries from the itinerary view.
            </p>
          </div>
          </div>
        </div>

        {/* Notification Preferences (collapsible) */}
        <div className="bg-white rounded-xl shadow-medium overflow-hidden">
          <button
            type="button"
            onClick={() => setNotifSectionOpen(!notifSectionOpen)}
            className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 hover:from-amber-100 hover:to-orange-100 transition-all"
          >
            <h2 className="text-xl font-bold text-amber-700 flex items-center gap-2">
              <FaBell className="text-amber-500" />
              Notification Preferences
            </h2>
            {notifSectionOpen ? (
              <FaChevronUp className="text-amber-400" />
            ) : (
              <FaChevronDown className="text-amber-400" />
            )}
          </button>

          {notifSectionOpen && (
            <div className="px-6 pb-6 space-y-5 pt-5">
              <p className="text-sm text-neutral-600 p-4 bg-amber-50 rounded-lg border-l-4 border-amber-400">
                Control which notifications you receive about your itineraries. Admin notifications and invitations to non-registered users are not affected.
              </p>

              <div className="bg-neutral-50 rounded-lg p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="enable_itinerary_notifications"
                    checked={formData.enable_itinerary_notifications}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-5 h-5 mt-0.5 rounded border-2 border-neutral-300 text-amber-500 focus:ring-2 focus:ring-amber-400 transition-colors cursor-pointer disabled:opacity-50"
                  />
                  <div>
                    <span className="font-semibold text-neutral-800 flex items-center gap-2 mb-1">
                      {formData.enable_itinerary_notifications ? (
                        <FaBell className="text-amber-500" />
                      ) : (
                        <FaBellSlash className="text-neutral-400" />
                      )}
                      Itinerary notifications
                    </span>
                    <p className="text-sm text-neutral-600">
                      {formData.enable_itinerary_notifications
                        ? "Receive email and in-app notifications for itinerary creation, sharing, modifications, and group member changes"
                        : "Itinerary notifications are disabled. You will not receive emails or in-app alerts for itinerary activity."}
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Scheduled Emails (collapsible) */}
        <div className="bg-white rounded-xl shadow-medium overflow-hidden">
          <button
            type="button"
            onClick={() => setScheduledSectionOpen(!scheduledSectionOpen)}
            className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 hover:from-blue-100 hover:to-indigo-100 transition-all"
          >
            <h2 className="text-xl font-bold text-blue-700 flex items-center gap-2">
              <FaClock className="text-blue-500" />
              Scheduled Emails
            </h2>
            {scheduledSectionOpen ? (
              <FaChevronUp className="text-blue-400" />
            ) : (
              <FaChevronDown className="text-blue-400" />
            )}
          </button>

          {scheduledSectionOpen && (
            <div className="px-6 pb-6 space-y-5 pt-5">
              <p className="text-sm text-neutral-600 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                These emails are sent on a schedule and are not triggered by user actions. They run daily at 8 AM UTC.
              </p>

              {/* Trip Reminders */}
              <div className="bg-neutral-50 rounded-lg p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="enable_trip_reminders"
                    checked={formData.enable_trip_reminders}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-5 h-5 mt-0.5 rounded border-2 border-neutral-300 text-blue-500 focus:ring-2 focus:ring-blue-400 transition-colors cursor-pointer disabled:opacity-50"
                  />
                  <div>
                    <span className="font-semibold text-neutral-800 flex items-center gap-2 mb-1">
                      <FaMapMarkedAlt className="text-blue-500" />
                      Trip reminders
                    </span>
                    <p className="text-sm text-neutral-600">
                      {formData.enable_trip_reminders
                        ? "Receive a reminder email before your trips with weather forecast, packing status, and group member info"
                        : "Trip reminder emails are disabled."}
                    </p>
                  </div>
                </label>
              </div>

              {/* Travel Advisories */}
              <div className="bg-neutral-50 rounded-lg p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="enable_travel_advisories"
                    checked={formData.enable_travel_advisories}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-5 h-5 mt-0.5 rounded border-2 border-neutral-300 text-blue-500 focus:ring-2 focus:ring-blue-400 transition-colors cursor-pointer disabled:opacity-50"
                  />
                  <div>
                    <span className="font-semibold text-neutral-800 flex items-center gap-2 mb-1">
                      <FaExclamationTriangle className="text-orange-500" />
                      Travel advisories
                    </span>
                    <p className="text-sm text-neutral-600">
                      {formData.enable_travel_advisories
                        ? "Receive email alerts about critical and warning-level travel advisories for your upcoming destinations"
                        : "Travel advisory emails are disabled. Advisories are still visible in your itinerary view."}
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Security Alerts (collapsible) */}
        <div className="bg-white rounded-xl shadow-medium overflow-hidden">
          <button
            type="button"
            onClick={() => setSecuritySectionOpen(!securitySectionOpen)}
            className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-red-50 to-rose-50 border-b border-red-200 hover:from-red-100 hover:to-rose-100 transition-all"
          >
            <h2 className="text-xl font-bold text-red-700 flex items-center gap-2">
              <FaShieldAlt className="text-red-500" />
              Security Alerts
            </h2>
            {securitySectionOpen ? (
              <FaChevronUp className="text-red-400" />
            ) : (
              <FaChevronDown className="text-red-400" />
            )}
          </button>

          {securitySectionOpen && (
            <div className="px-6 pb-6 space-y-5 pt-5">
              <p className="text-sm text-neutral-600 p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
                Security alerts help protect your account by notifying you of important security events.
              </p>

              <div className="bg-neutral-50 rounded-lg p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="enable_security_alerts"
                    checked={formData.enable_security_alerts}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-5 h-5 mt-0.5 rounded border-2 border-neutral-300 text-red-500 focus:ring-2 focus:ring-red-400 transition-colors cursor-pointer disabled:opacity-50"
                  />
                  <div>
                    <span className="font-semibold text-neutral-800 flex items-center gap-2 mb-1">
                      <FaShieldAlt className="text-red-500" />
                      Password change notifications
                    </span>
                    <p className="text-sm text-neutral-600">
                      {formData.enable_security_alerts
                        ? "Receive an email notification whenever your password is changed"
                        : "Password change notifications are disabled. We recommend keeping this enabled for security."}
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Marketing Preferences (collapsible) */}
        <div className="bg-white rounded-xl shadow-medium overflow-hidden">
          <button
            type="button"
            onClick={() => setMarketingSectionOpen(!marketingSectionOpen)}
            className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-200 hover:from-purple-100 hover:to-pink-100 transition-all"
          >
            <h2 className="text-xl font-bold text-purple-700 flex items-center gap-2">
              <FaBullhorn className="text-purple-500" />
              Marketing Preferences
            </h2>
            {marketingSectionOpen ? (
              <FaChevronUp className="text-purple-400" />
            ) : (
              <FaChevronDown className="text-purple-400" />
            )}
          </button>

          {marketingSectionOpen && (
            <div className="px-6 pb-6 space-y-5 pt-5">
              <p className="text-sm text-neutral-600 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                Control whether you receive promotional emails about new features, special offers, and travel tips.
              </p>

              <div className="bg-neutral-50 rounded-lg p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="marketing_consent"
                    checked={formData.marketing_consent}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-5 h-5 mt-0.5 rounded border-2 border-neutral-300 text-purple-500 focus:ring-2 focus:ring-purple-400 transition-colors cursor-pointer disabled:opacity-50"
                  />
                  <div>
                    <span className="font-semibold text-neutral-800 flex items-center gap-2 mb-1">
                      <FaBullhorn className="text-purple-500" />
                      Marketing emails
                    </span>
                    <p className="text-sm text-neutral-600">
                      {formData.marketing_consent
                        ? "Receive emails about new features, travel tips, special offers, and partner deals"
                        : "You won't receive marketing or promotional emails from us."}
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Change Password Section */}
        <div className="bg-white rounded-xl shadow-medium overflow-hidden">
          <button
            type="button"
            onClick={() => {
              setPasswordOpen(!passwordOpen);
              setPasswordError('');
              setPasswordMessage('');
            }}
            className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-red-50 to-rose-50 border-b border-red-200 hover:from-red-100 hover:to-rose-100 transition-all"
          >
            <h2 className="text-xl font-bold text-red-700 flex items-center gap-2">
              <FaLock className="text-red-500" />
              Change Password
            </h2>
            {passwordOpen ? (
              <FaChevronUp className="text-red-400" />
            ) : (
              <FaChevronDown className="text-red-400" />
            )}
          </button>

          {passwordOpen && (
            <div className="px-6 pb-6 space-y-5 pt-5">
              {/* Password success message */}
              {passwordMessage && (
                <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-lg flex items-center gap-3 animate-slide-down">
                  <FaCheck className="text-xl flex-shrink-0" />
                  <p className="font-medium">{passwordMessage}</p>
                </div>
              )}

              {/* Password error message */}
              {passwordError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg flex items-center gap-3 animate-slide-down">
                  <FaTimes className="text-xl flex-shrink-0" />
                  <p className="font-medium">{passwordError}</p>
                </div>
              )}

              {/* Current Password */}
              <div>
                <label htmlFor="currentPassword" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
                  <FaLock className="text-red-500" />
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-red-500 focus:ring-4 focus:ring-red-100 outline-none transition-all disabled:opacity-50 pr-12"
                    disabled={passwordLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label htmlFor="newPassword" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
                  <FaLock className="text-primary-500" />
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all disabled:opacity-50 pr-12"
                    disabled={passwordLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* Confirm New Password */}
              <div>
                <label htmlFor="confirmNewPassword" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
                  <FaLock className="text-primary-500" />
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmNewPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    className={`w-full px-4 py-3 rounded-lg border-2 transition-all disabled:opacity-50 pr-12 outline-none ${
                      confirmPassword && !passwordChecks.match
                        ? 'border-red-500 bg-red-50'
                        : 'border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100'
                    }`}
                    disabled={passwordLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {confirmPassword && !passwordChecks.match && (
                  <p className="text-red-600 text-sm mt-1">Passwords do not match</p>
                )}
              </div>

              {/* Password Requirements Checklist */}
              <div className="p-4 bg-neutral-50 rounded-lg">
                <p className="text-sm font-semibold text-neutral-700 mb-3">Password Requirements:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { key: 'minLength', label: 'At least 8 characters' },
                    { key: 'uppercase', label: 'One uppercase letter (A-Z)' },
                    { key: 'lowercase', label: 'One lowercase letter (a-z)' },
                    { key: 'digit', label: 'One digit (0-9)' },
                    { key: 'special', label: 'One special character (!@#$...)' },
                    { key: 'match', label: 'Passwords match' },
                  ].map(({ key, label }) => (
                    <div
                      key={key}
                      className={`flex items-center gap-2 text-sm transition-colors ${
                        passwordChecks[key] ? 'text-green-600' : 'text-neutral-500'
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                        passwordChecks[key] ? 'bg-green-500' : 'bg-neutral-300'
                      }`}>
                        {passwordChecks[key] && <FaCheck className="text-white text-[8px]" />}
                      </span>
                      {label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Change Password Button */}
              <button
                type="button"
                onClick={handlePasswordSubmit}
                disabled={passwordLoading || !allPasswordChecksPass || !currentPassword}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-6 rounded-lg font-semibold shadow-travel hover:shadow-elevated hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {passwordLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Changing Password...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <FaLock />
                    Change Password
                  </span>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Success/Error Message */}
        {message && (
          <div
            className={`rounded-lg p-4 flex items-center gap-3 animate-slide-down ${
              message.includes('success')
                ? 'bg-green-50 border-l-4 border-green-500 text-green-700'
                : 'bg-red-50 border-l-4 border-red-500 text-red-700'
            }`}
          >
            {message.includes('success') && <FaCheck className="text-xl" />}
            <p className="font-medium">{message}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-gradient-primary text-white py-3 px-6 rounded-lg font-semibold shadow-travel hover:shadow-elevated hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving Changes...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <FaCheck />
                Save Changes
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="sm:flex-initial px-6 py-3 rounded-lg font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfile;
